import { signOut } from '@backoffice-frontend/authentication'
import { LOGIN_INTENDED_PATH } from '@backoffice-frontend/common'
import { MoiaLogo } from '@backoffice-frontend/patterns'
import { Button, Callout, Card, Heading, Text } from '@moia-dev/pace-web'
import { useTranslation } from 'react-i18next'
import store from 'store'

type WrongSubdomainViewProps = {
  title?: string
  expectedUrl?: string
}

export const WrongSubdomainView = ({
  title,
  expectedUrl,
}: WrongSubdomainViewProps) => {
  const { t } = useTranslation()

  // Sign out must never depend on resolving the URL, otherwise a failed lookup
  // leaves the user on a screen with no way out.
  const handleSignOut = async () => {
    store.remove(LOGIN_INTENDED_PATH)
    await signOut()
  }

  // Tokens live per origin, so the correct Backoffice asks for a sign-in again.
  // Dropping the intended path keeps the sign-in on this origin from sending
  // them back to a page they may not be allowed on.
  const handleGoToCorrectUrl = () => {
    if (!expectedUrl) {
      return
    }
    store.remove(LOGIN_INTENDED_PATH)
    window.location.assign(expectedUrl)
  }

  return (
    <>
      <title>{t('authenticatedAppView_wrongSubdomain_title')}</title>
      <div
        css={{
          minHeight: '100vh',
          backgroundSize: 'cover',
          alignItems: 'flex-start',
          justifyItems: 'center',
          backgroundImage: 'url(/assets/images/login.png)',
          display: 'grid',
        }}
      >
        <MoiaLogo
          css={theme => ({
            width: '160px',
            marginTop: theme.space.Space6,
            color: theme.palette.common.white,
          })}
        />
        <Card
          css={theme => ({
            padding: theme.space.Space4,
            width: '384px',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space4,
          })}
          outline
        >
          {title && (
            <Heading css={{ textAlign: 'center' }} size="xl">
              {title}
            </Heading>
          )}
          <Callout
            variant="warning"
            title={t('authenticatedAppView_wrongSubdomain_calloutTitle')}
            hideDismiss
          >
            {expectedUrl
              ? t('authenticatedAppView_wrongSubdomain_messageWithUrl')
              : t('authenticatedAppView_wrongSubdomain_message')}
          </Callout>
          {expectedUrl && (
            <div css={{ display: 'flex', flexDirection: 'column' }}>
              <Text size="sm">
                {t('authenticatedAppView_wrongSubdomain_correctUrlLabel')}
              </Text>
              <Text size="sm">{expectedUrl}</Text>
            </div>
          )}
          {expectedUrl && (
            <Button
              onClick={handleGoToCorrectUrl}
              label={t('authenticatedAppView_wrongSubdomain_goThereButton')}
            />
          )}
          <Button
            variant={expectedUrl ? 'secondary' : 'primary'}
            onClick={handleSignOut}
            label={t('authenticatedAppView_wrongSubdomain_signOutButton')}
          />
        </Card>
      </div>
    </>
  )
}
